/**
 * Turns raw key events into press/release of the companion's activation
 * CHORD: Ctrl+Alt (Ctrl+Option on Mac), held with nothing else.
 *
 * Why a two-key chord: the old single-key gesture (bare Ctrl) collided with
 * Ctrl+wheel chart zoom, the most common charting gesture (2026-10-01: a
 * trader's every Ctrl-zoom release froze the page). No chart or app binding
 * holds Ctrl and Alt together. Mirrors Clicky's Ctrl+Option default.
 *
 * Feeds the timing recognizer (ctrl-gesture.ts), which decides tap / double-
 * tap / hold. Pure: no DOM listeners, so it's unit-testable.
 */
import type { CtrlGesture } from './ctrl-gesture'

/** The KeyboardEvent fields the chord reads. */
export type ChordKeyEvent = Pick<
  KeyboardEvent,
  'key' | 'ctrlKey' | 'altKey' | 'metaKey' | 'shiftKey' | 'repeat' | 'target' | 'getModifierState'
>

export interface ActivationChord {
  keyDown(e: ChordKeyEvent): void
  keyUp(e: ChordKeyEvent): void
  blur(): void
}

const CHORD_KEYS = new Set(['Control', 'Alt'])

function isAltGraph(e: ChordKeyEvent): boolean {
  // AltGr reports as Ctrl+Alt on many European Windows layouts (it types @, €).
  return e.key === 'AltGraph' || e.getModifierState('AltGraph')
}

export function createActivationChord(
  gesture: Pick<CtrlGesture, 'keyDown' | 'keyUp' | 'otherKeyDown' | 'blur'>,
  opts: { shouldIgnoreTarget?: (target: EventTarget | null) => boolean } = {},
): ActivationChord {
  let chordDown = false

  return {
    keyDown(e) {
      if (!CHORD_KEYS.has(e.key)) {
        // Any other key while the chord is held makes it a shortcut, not a
        // tap or hold (AltGr included).
        if (chordDown) gesture.otherKeyDown()
        return
      }
      if (chordDown || e.repeat) return
      if (!e.ctrlKey || !e.altKey || e.shiftKey || e.metaKey || isAltGraph(e)) return
      if (opts.shouldIgnoreTarget?.(e.target)) return
      chordDown = true
      gesture.keyDown()
    },

    keyUp(e) {
      if (!chordDown || !CHORD_KEYS.has(e.key)) return
      chordDown = false
      gesture.keyUp()
    },

    blur() {
      chordDown = false
      gesture.blur()
    },
  }
}
